import { useState, useRef, useCallback } from 'react';

export type StepStatus = 'idle' | 'running' | 'success' | 'error';

export interface PipelineContext {
    results: Record<string, unknown>;
}

export interface PipelineStepConfig {
    id: string;
    label: string;
    apiCall: (context: PipelineContext) => Promise<unknown>;
}

interface StepState {
    status: StepStatus;
    error?: string;
}

interface UsePipelineReturn {
    stepStates: Record<string, StepState>;
    start: () => void;
    retry: (stepId: string) => void;
    isRunning: boolean;
    isFinished: boolean;
    hasStarted: boolean;
}

const usePipeline = (steps: PipelineStepConfig[]): UsePipelineReturn => {
    const buildInitialStates = (): Record<string, StepState> => Object.fromEntries(steps.map((s) => [s.id, { status: 'idle' as StepStatus }]));

    const [stepStates, setStepStates] = useState<Record<string, StepState>>(buildInitialStates);
    const [hasStarted, setHasStarted] = useState(false);
    const resultsRef = useRef<Record<string, unknown>>({});
    const isRunningRef = useRef(false);

    const setStepStatus = useCallback((id: string, status: StepStatus, error?: string) => {
        setStepStates((prev) => ({ ...prev, [id]: { status, error } }));
    }, []);

    const runFromStep = useCallback(async (fromIndex: number) => {
        if (isRunningRef.current) { return; }
        isRunningRef.current = true;

        for (let i = fromIndex; i < steps.length; i++) {
            const step = steps[i];
            setStepStatus(step.id, 'running');
            try {
                // eslint-disable-next-line no-await-in-loop
                const result = await step.apiCall({ results: resultsRef.current });
                resultsRef.current[step.id] = result;
                setStepStatus(step.id, 'success');
            } catch (error: unknown) {
                const message = error instanceof Error ? error.message : String(error);
                setStepStatus(step.id, 'error', message);
                isRunningRef.current = false;

                return;
            }
        }

        isRunningRef.current = false;
    }, [steps, setStepStatus]);

    const start = useCallback(() => {
        if (isRunningRef.current) { return; }
        resultsRef.current = {};
        setStepStates(buildInitialStates());
        setHasStarted(true);
        runFromStep(0);
    }, [steps, runFromStep]);

    const retry = useCallback((stepId: string) => {
        const fromIndex = steps.findIndex((s) => s.id === stepId);
        if (fromIndex === -1 || isRunningRef.current) { return; }

        for (let i = fromIndex; i < steps.length; i++) {
            delete resultsRef.current[steps[i].id];
        }

        setStepStates((prev) => {
            const next = { ...prev };
            for (let i = fromIndex; i < steps.length; i++) {
                next[steps[i].id] = { status: 'idle' };
            }

            return next;
        });

        runFromStep(fromIndex);
    }, [steps, runFromStep]);

    const isRunning = Object.values(stepStates).some((s) => s.status === 'running');
    const isFinished = steps.length > 0 && Object.values(stepStates).every((s) => s.status === 'success');

    return {
        stepStates, start, retry, isRunning, isFinished, hasStarted,
    };
};

export default usePipeline;
